<template>
  <div class="readout">
    <div
        v-for="vital in vitals"
        :key="vital.key"
        class="readout__vital"
        :class="[`is-${vital.key}`, {'is-critical': vital.critical, 'is-blank': !vital.known}]"
    >
      <p class="readout__line">
        <span class="readout__label">{{ vital.label }}</span>
        <span class="readout__value">{{ vital.text }}</span>
      </p>

      <span v-if="vital.discrete" class="readout__pips" :title="vital.title">
        <span
            v-for="pip in vital.pips"
            :key="pip.index"
            class="readout__pip"
            :class="{'is-spent': !pip.filled}"
        ></span>
      </span>
      <span v-else class="readout__track" :title="vital.title">
        <span class="readout__fill" :style="{width: vital.percent + '%'}"></span>
      </span>
    </div>
  </div>
</template>

<script setup>
import {computed} from 'vue';

const props = defineProps({
  details: {
    type: Object,
    required: true,
  },
});

/*
 * The server names attributes "Health" / "Energy" / "Action Points"
 * (`AttributeType`, apps/core/models/core.py:580) and Location.vue lowercases
 * that name verbatim, so the third key arrives as "action points" — with a
 * space. Keying the display config on "action" therefore matched nothing and
 * action points rendered unlabelled in the fallback grey. Every spelling the
 * three callers can produce is folded to one slot here instead.
 */
const SLOTS = {
  health: 'health',
  hp: 'health',
  energy: 'energy',
  en: 'energy',
  'action points': 'action',
  action_points: 'action',
  actionpoints: 'action',
  action: 'action',
  ap: 'action',
};

/* Fixed reading order, independent of the key order the payload happens to
   carry: a bar that changes position between two characters cannot be
   compared across a roster at a glance. */
const ORDER = [
  {key: 'health', label: 'HP', name: 'Health', discrete: false},
  {key: 'energy', label: 'EN', name: 'Energy', discrete: false},
  /* Action points are countable and small — a handful, not a magnitude — so
     they are drawn as pips rather than as a bar. Same distinction the game
     master's AttributeBar already makes. */
  {key: 'action', label: 'AP', name: 'Action Points', discrete: true},
];

/* Location.vue substitutes the string "xx" for anything the inspection did not
   return, so a reading is only a reading when it is actually a number. */
const num = value => (Number.isFinite(Number(value)) && value !== '' && value !== null ? Number(value) : null);

const vitals = computed(() => {
  const source = props.details?.attributes || {};
  const bySlot = {};
  for (const [rawKey, attr] of Object.entries(source)) {
    const slot = SLOTS[String(rawKey).toLowerCase().trim()];
    if (slot && !bySlot[slot]) bySlot[slot] = attr;
  }

  return ORDER.filter(spec => bySlot[spec.key]).map(spec => {
    const attr = bySlot[spec.key];
    const current = num(attr.current);
    const max = num(attr.max);
    const known = current !== null && max !== null && max > 0;
    const percent = known ? Math.max(0, Math.min(100, (current / max) * 100)) : 0;

    return {
      ...spec,
      known,
      percent,
      text: known ? `${current}/${max}` : '—',
      title: known ? `${spec.name}: ${current} of ${max}` : `${spec.name}: unknown`,
      critical: spec.key === 'health' && known && percent <= 25,
      /* Ten pips is the readable ceiling at this width; above it the pips
         become a texture rather than a count, so the number carries it. */
      pips: known && max <= 10
          ? Array.from({length: max}, (_, index) => ({index, filled: index < current}))
          : [],
    };
  }).map(vital => ({
    ...vital,
    discrete: vital.discrete && vital.pips.length > 0,
  }));
});
</script>

<style scoped>
/* Vitals are drawn in the environmental register the game master's
   AttributeBar established — banked blood, cold steel, worn brass — not in the
   flat material red/blue/violet this card used to carry. The palette is dark
   enough to sit on a photograph without punching a hole in it. */
.readout {
  display: flex;
  flex-direction: column;
  gap: 0.14rem;
}

.readout__vital {
  --vital-lo: #6f2521;
  --vital-hi: #d7574a;
  --vital-h: 0.19rem;
}

.readout__vital.is-energy {
  --vital-lo: #3d6480;
  --vital-hi: #8fbcdd;
  --vital-h: 0.14rem;
}

.readout__vital.is-action {
  --vital-lo: #7d6a37;
  --vital-hi: #d8a44c;
  --vital-h: 0.14rem;
}

.readout__line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.2rem;
  margin: 0 0 0.06rem;
  font-family: var(--pl-font-mono, ui-monospace, monospace);
  font-size: 0.44rem;
  line-height: 1;
  letter-spacing: 0.02em;
}

.readout__label {
  color: rgba(223, 233, 238, 0.42);
  letter-spacing: 0.1em;
}

.readout__value {
  color: #dfe9ee;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

.is-critical .readout__value {
  color: var(--vital-hi);
}

.is-blank .readout__value {
  color: rgba(223, 233, 238, 0.35);
}

.readout__track {
  display: block;
  height: var(--vital-h);
  background: rgba(0, 0, 0, 0.62);
  box-shadow: inset 0 0 0 1px rgba(223, 233, 238, 0.1);
  overflow: hidden;
}

.readout__fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--vital-lo), var(--vital-hi));
  transition: width 0.35s var(--pl-ease, ease);
}

/* The one animated thing on the card, and it fires only when a character is
   within a hit of going down. Anything that pulses all the time is decoration
   and stops being read. */
.is-critical .readout__fill {
  animation: vital-critical 1.6s ease-in-out infinite;
}

@keyframes vital-critical {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

.readout__pips {
  display: flex;
  gap: 1px;
  height: var(--vital-h);
}

.readout__pip {
  flex: 1;
  min-width: 1px;
  background: var(--vital-hi);
}

.readout__pip.is-spent {
  background: rgba(0, 0, 0, 0.62);
  box-shadow: inset 0 0 0 1px rgba(223, 233, 238, 0.1);
}

/* No reading — a broken line, so an unknown can never be misread as a drained
   bar. */
.is-blank .readout__track {
  background: repeating-linear-gradient(
      90deg,
      rgba(223, 233, 238, 0.16) 0 2px,
      transparent 2px 4px
  );
  box-shadow: none;
}

@media (prefers-reduced-motion: reduce) {
  .readout__fill {
    transition: none;
    animation: none;
  }
}
</style>
